<template>
  <div
    class="text-gray-600 md:block"
    :class="{ 'mb-6': bottomMargin }"
    @dragover.prevent
    @drop.prevent
  >
    <div
      v-for="(file, index) in files"
      :key="(file.file ? file.file.name : file.formio.originalName) + index"
      class="text-gray-600"
    >
      <div class="flex items-center">
        <button class="mr-2" @click="remove(index)">
          <svg
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-trash-2"
          >
            <polyline points="3 6 5 6 21 6" />
            <path
              d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
            />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>
        <span>{{ file.file ? file.file.name : file.formio.originalName }}</span>
      </div>
      <p>
        {{ file.status }}
        <a
          v-if="file.status === 'Upload failed'"
          :class="[
            secondaryColor
              ? `${secondaryColorClass}`
              : `${secondaryColorClass} hover:text-teal-900 focus:text-teal-900`,
          ]"
          class="font-semibold underline"
          href="#"
          @click="retryUpload(file.file, index)"
        >
          Retry
        </a>
      </p>
      <template v-if="file.status !== 'Upload failed'">
        <p v-for="(error, i) in file.errors" :key="i" class="text-error-900">
          {{ error }}
        </p>
      </template>
    </div>

    <div
      v-show="!edit"
      class="p-10 text-center text-black border-2 border-gray-200 rounded-lg bg-white"
      @drop="handleFileDrop"
      @dragover="handleFileDragover"
      @dragleave="handleFileDragleave"
    >
      <input
        :id="name"
        type="file"
        :name="name"
        class="w-px h-px opacity-0 overflow-hidden absolute"
        :accept="fileTypes.includes('*') ? '' : fileTypes"
        tabindex="-1"
        @change="handleFileInput"
      />
      <p v-if="customFileBoxLabel" class="mb-10 text-left">
        {{ customFileBoxLabel }}
      </p>
      <template v-else>
        <p class="mb-1">
          Drag and drop files here.
        </p>
      </template>
      <label :for="name" class="block cursor-pointer">
        <img
          :src="cropImg || placeholderImg"
          :class="['rounded m-auto', imageClasses]"
        />
        <div
          tabindex="0"
          :class="[
            widthButton,
            secondaryColor
              ? 'brandingSecondary'
              : 'bg-university-secondary hover:bg-teal-100 focus:bg-teal-100',
          ]"
          class="mt-6 inline-block text-center rounded font-normal border-2 border-transparent px-4 py-1 min-h-fit w-48 text-white hover:text-white focus:text-white focus:outline-none focus:ring"
          data-cy="uploadButton"
          @keyup.enter="handleFileClick"
        >
          <span>{{ customButtonLabel }}</span>
        </div>
      </label>
    </div>
    <SimpleTextDialog v-model="edit">
      <template #header>
        <h2 class="text-xl font-semibold">Crop Image</h2>
      </template>
      <div class="flex items-center justify-center">
        <VuePictureCropper
          ref="cropperRef"
          :box-style="{
            width: '100%',
            height: '100%',
            backgroundColor: '#f8f8f8',
            margin: 'auto',
          }"
          :img="imgSrc"
          :options="{
            viewMode: 1,
            dragMode: 'crop',
            aspectRatio: NaN,
            autoCropArea: 0.8,
            responsive: true,
          }"
          @ready="ready"
          @cropend="onCropEnd"
        />
      </div>
      <template #footer>
        <Button label="Upload" class="inline-flex w-full justify-center rounded-md border border-transparent bg-teal-900 px-4 py-2 text-base font-medium text-white hover:bg-teal-100 hover:text-white focus:bg-teal-100 focus:text-white focus:outline-none focus:ring sm:text-sm" @click="cropImage" />
      </template>
    </SimpleTextDialog>
  </div>
</template>

<script>
import { ERROR_TIMEOUT } from "@/constants.js";
import { calculateAspectRatio, getAspectRatioClasses } from "@/util/aspectRatio";
import { hasDotPrefix, isLowerCase } from "@/util/string";
import { datadogLogs } from "@datadog/browser-logs";
import ImgPlaceholder from "@images/api-wl-icon-placeholder.png";
import VuePictureCropper, { cropper } from "vue-picture-cropper";
import { useToast } from "vue-toast-notification";
import s3Helper from "../../../mixins/s3UniversityHelper.js";
import sentryHelper from "../../../mixins/sentryHelper.js";
import SimpleTextDialog from "@/components/CurriculumIntegrationApp/components/SimpleTextDialog.vue";
import Button from "primevue/button";

export default {
  name: "ImageUpload",
  components: {
    VuePictureCropper,
    SimpleTextDialog,
    Button,
  },
  mixins: [s3Helper, sentryHelper],
  props: {
    name: {
      type: String,
      default: "imageUpload",
    },
    fileTypes: {
      type: Array,
      default: () => [".jpg", ".jpeg", ".png"],
      validator(value) {
        return hasDotPrefix(value, "<ImageUpload /> prop error") && isLowerCase(value, "<ImageUpload /> prop error");
      },
    },
    maxFileSize: {
      type: Number,
      default: 3,
    },
    formioFiles: {
      type: Array,
      default: () => [],
    },
    customButtonLabel: {
      type: String,
      default: "Upload Files",
    },
    customFileBoxLabel: {
      type: String,
      default: "",
    },
    bottomMargin: {
      type: Boolean,
      default: true,
    },
    widthButton: {
      type: String,
      default: "",
    },
    placeholderImg: {
      type: String,
      default: ImgPlaceholder,
    },
  },
  emits: ["updateProgress", "updateFiles", "s3Location"],
  data() {
    return {
      files: [],
      imgSrc: "",
      cropImg: "",
      fName: "",
      edit: false,
      aspectRatios: [1, 1.5, 2.86, 3, 3.97, 5.15],
      aspectRatio: null,
      filesOnProgress: 0,
    };
  },
  computed: {
    imageClasses() {
      if (!this.cropImg || !this.aspectRatio) {
        return "w-48 h-48 aspect-1";
      }
      const { classes, aspectClass } = getAspectRatioClasses(this.aspectRatio);
      return `${classes} ${aspectClass}`;
    },
    hasErrors() {
      return this.files.some((file) => file.errors.length);
    },
    lowerCaseFileTypes() {
      return this.fileTypes.map((fileType) => fileType.toLowerCase());
    },
  },
  watch: {
    filesOnProgress(val) {
      /*
        *****IMPORTANT***** Create updateProgress method on the parent component.
        This will return true if files still in upload progress
      */
      this.$emit("updateProgress", val > 0);
    },
    files: {
      handler(val) {
        const formioFiles = val.filter((file) => Object.keys(file.formio).length > 0).map((file) => file.formio);
        /*
          *****IMPORTANT***** Create updateFiles method on the parent component.
          This will return an array of uploaded files and
          a boolean that will tell if the file upload has files with errors
        */
        this.$emit("updateFiles", formioFiles, this.hasErrors);
      },
      deep: true,
    },
    formioFiles: {
      handler(val) {
        if (Array.isArray(val) && val.length && this.files.length !== val.length) {
          this.addExistingFiles();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.addExistingFiles();
  },
  methods: {
    fileKey(file, index) {
      return `${this.fileName(file)}-${index}`;
    },
    fileName(file) {
      return file.file ? file.file.name : file.formio.originalName;
    },
    onCropperReady() {
      cropper.setAspectRatio(Number.NaN);

      const defaultAspectRatio = 1;

      const imageData = cropper.getImageData();
      const imageWidth = imageData.naturalWidth;
      const imageHeight = imageData.naturalHeight;

      let cropBoxWidth, cropBoxHeight;

      if (imageWidth / imageHeight >= defaultAspectRatio) {
        cropBoxHeight = imageHeight * 0.8;
        cropBoxWidth = cropBoxHeight * defaultAspectRatio;
      } else {
        cropBoxWidth = imageWidth * 0.8;
        cropBoxHeight = cropBoxWidth / defaultAspectRatio;
      }

      const cropBoxLeft = (imageWidth - cropBoxWidth) / 2;
      const cropBoxTop = (imageHeight - cropBoxHeight) / 2;

      cropper.setCropBoxData({
        left: cropBoxLeft,
        top: cropBoxTop,
        width: cropBoxWidth,
        height: cropBoxHeight,
      });
    },
    onCropEnd() {
      const cropBoxData = cropper.getCropBoxData();
      const currentAspectRatio = cropBoxData.width / cropBoxData.height;

      const closestAspectRatio = this.aspectRatios.reduce((prev, curr) => {
        return Math.abs(curr - currentAspectRatio) < Math.abs(prev - currentAspectRatio) ? curr : prev;
      });

      this.adjustCropBoxToAspectRatio(closestAspectRatio);
    },
    adjustCropBoxToAspectRatio(aspectRatio) {
      const cropBoxData = cropper.getCropBoxData();
      const imageData = cropper.getImageData();

      const centerX = cropBoxData.left + cropBoxData.width / 2;
      const centerY = cropBoxData.top + cropBoxData.height / 2;

      let newWidth, newHeight;

      if (cropBoxData.width / cropBoxData.height >= aspectRatio) {
        newHeight = cropBoxData.height;
        newWidth = newHeight * aspectRatio;
      } else {
        newWidth = cropBoxData.width;
        newHeight = newWidth / aspectRatio;
      }

      const maxLeft = imageData.width - newWidth;
      const maxTop = imageData.height - newHeight;
      let left = centerX - newWidth / 2;
      let top = centerY - newHeight / 2;

      left = Math.max(0, Math.min(left, maxLeft));
      top = Math.max(0, Math.min(top, maxTop));

      cropper.setCropBoxData({
        left,
        top,
        width: newWidth,
        height: newHeight,
      });
    },
    async cropImage() {
      if (!cropper) return;

      const base64 = cropper.getCroppedCanvas().toDataURL();
      this.cropImg = base64;

      const cropBoxData = cropper.getData();
      const width = cropBoxData.width;
      const height = cropBoxData.height;
      this.aspectRatio = calculateAspectRatio(width, height);

      cropper.getCroppedCanvas().toBlob((blob) => {
        const croppedFile = new File([blob], this.fName, { type: blob.type });
        this.addFile(croppedFile);
        this.edit = false;
      });
    },
    handleFileDrop(e) {
      e.currentTarget.classList.add("bg-gray-50");

      const droppedFiles = e.dataTransfer.files;
      if (!droppedFiles || !droppedFiles.length) return;

      this.processFile(droppedFiles[0]);
    },
    handleFileDragover(e) {
      e.currentTarget.classList.remove("bg-gray-50");
    },
    handleFileDragleave(e) {
      e.currentTarget.classList.add("bg-gray-50");
    },
    handleFileInput(e) {
      const files = e.target.files;
      if (!files || !files.length) return;

      this.processFile(files[0]);

      e.target.value = "";
    },
    handleFileClick() {
      document.getElementById(this.name).click();
    },
    processFile(file) {
      this.remove(0);

      this.fName = file.name;

      if (!this.isValidExtension(this.fName)) {
        this.addFile(file);
      } else {
        this.readFile(file);
      }
    },
    remove(index) {
      if (index >= 0 && index < this.files.length) {
        this.files.splice(index, 1);
      }
      this.cropImg = "";
      this.imgSrc = "";
      this.aspectRatio = null;
      this.$emit('imageRemoved');
    },
    readFile(file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.imgSrc = reader.result;
        this.aspectRatio = 1;
        this.edit = true;
      };
      reader.readAsDataURL(file);
    },
    addFile(file) {
      const fileObject = {
        file: new File([file], file.name),
        errors: [],
        status: "Validating file...",
        formio: {},
      };

      if (!this.isValidExtension(file.name)) {
        fileObject.status = "";
        fileObject.errors.push(`File is the wrong type; it must be ${this.fileTypes.join(", ")}`);
      }

      if (!this.isValidSize(file.size)) {
        fileObject.status = "";
        fileObject.errors.push(`File is too big; it must be at most ${this.maxFileSize}MB`);
      }

      this.files.push(fileObject);

      if (!fileObject.errors.length) {
        this.uploadFile(fileObject.file, this.files.length - 1);
      }
    },
    retryUpload(file, index) {
      this.files[index].errors = [];
      this.files[index].status = "Starting upload...";

      this.uploadFile(file, index);
    },
    async uploadFile(file, index) {
      const newFile = this.files[index];
      const newName = this.createUUIDName(file.name);

      this.filesOnProgress++;
      newFile.status = "Starting upload...";

      try {
        const data = await this.s3Upload(file, newName);
        this.filesOnProgress--;
        newFile.status = "";
        newFile.formio = this.createFormioObject(file, newName, data);
        this.$emit("s3Location", newFile.formio);
      } catch (error) {
        this.filesOnProgress--;
        newFile.status = "Upload failed";
        newFile.errors.push(error.message);

        const toast = useToast();
        toast.open({
          message: error.message,
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
        datadogLogs.logger.error(`S3 upload: ${error.message}`, { data: this.name }, error);
      }
    },
    isValidExtension(fileName) {
      const extension = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
      return this.fileTypes.includes("*") || this.lowerCaseFileTypes.includes(extension);
    },
    isValidSize(fileSize) {
      const sizeInMB = fileSize / (1024 * 1024);
      return sizeInMB <= this.maxFileSize;
    },
    addExistingFiles() {
      this.formioFiles.forEach((f) => {
        const fileObject = {
          file: null,
          errors: [],
          status: "",
          formio: f,
        };
        this.files.push(fileObject);
      });
    },
    createFormioObject(file, newName, s3) {
      return {
        storage: "s3",
        name: newName,
        bucket: s3.Bucket,
        key: s3.key,
        url: s3.Location,
        acl: "private",
        size: file.size,
        type: file.type,
        originalName: file.name,
      };
    },
    removeAllFiles() {
      for (let i = this.files.length - 1; i >=0; i--) {
        this.remove(i);
      };
    },
  },
};
</script>
